import {
    pick as _pick,
    isEmpty as _isEmpty,
} from '@lodash';
import RestService from '@core/services/authentication/_private/RestService';
import LoginCredentials from '@src/core/models/authentication/LoginCredentials';
import User  from '@core/models/user/User';
import AuthenticationError from '@errors/authentication/AuthenticationError';
import RegisterInput from '@countryModels/authentication/RegisterInput';
import MalformedDataError from '@errors/network/MalformedDataError';
import ValidationError from '@errors/network/ValidationError';
import * as NetworkUtils from '@utils/network/NetworkUtils';
import eventChannel from '@core/tracking/EventChannel';
import Topic from '@core/tracking/Topic';
import {
    REGISTERED_SUCCESSFULLY,
    ACCOUNT_ACTIVATED_SUCCESSFULLY
} from '@core/tracking/Events';

export default class AuthenticationService {
    private restService: RestService = new RestService();

    /**
     * @throws {Error}
     */
    public async register(registerInput: RegisterInput): Promise<any> {
        const setFieldErrorIfExist = (name: string, fieldErrors: any) => {
            if (response.fieldErrors[name]) {
                fieldErrors[name] = [response.fieldErrors[name]];
            }
        };

        const response = await this.restService.register(registerInput);

        if (response.error) {
            if (!_isEmpty(response.fieldErrors)) {
                const fieldErrors = {};

                Object.keys(registerInput).forEach((key: string) => {
                    setFieldErrorIfExist(key, fieldErrors);
                });

                throw new ValidationError(fieldErrors);
            } else if (response.notice) {
                const fieldErrors: any = {};
                fieldErrors['general'] = [response.notice];
                throw new ValidationError(fieldErrors);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }

        eventChannel.publish(Topic.Analytics, {
            event: REGISTERED_SUCCESSFULLY,
            username: registerInput.username,
        });
    }

    public async isRegisterFieldValid(key: string, value: string): Promise<any> {
        // BEWARE: isRegisterFieldValid has cancelable request!
        const response = await this.restService.isRegisterFieldValid(key, value);
        if (response && response.error) {
            throw new ValidationError({
                [key]: [response.fieldErrors[key]]
            });
        }
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async login(credentials: LoginCredentials): Promise<User> {
        const response = await this.restService.login(credentials);

        if (response.error) {
            if (response.errorCode === 'PlayerAccountError') {
                throw new ValidationError({
                    nonField: ['you must activate your account']
                });
            } else if (response.errorCode === 'PrincipalNotFound' || response.errorCode === 'InvalidCredential') {
                throw new ValidationError({
                    message: response.notice
                });
            }  else if (response.notice) {
                throw new ValidationError({
                    message: response.notice
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }

        return this.createUser(response.data);
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getSession(): Promise<User> {
        const response = await this.restService.getSession();

        if (response.error) {
            throw new AuthenticationError();
        }

        return this.createUser(response.data);
    }

    public async isAuthenticated() {
        const response = await this.restService.getSession();
        return !response.error;
    }

    /**
     * @throws {AuthenticationError}
     * @throws {RequestError}
     * @throws {Error}
     */
    public logout(): Promise<void> {
        return NetworkUtils.processResponse(this.restService.logout());
    }

    public async activateAccount(token: string): Promise<User> {
        await NetworkUtils.processResponse(this.restService.activateAccount(token));
        const session = await this.getSession();

        eventChannel.publish(Topic.Analytics, {
            event: ACCOUNT_ACTIVATED_SUCCESSFULLY,
            username: session.username,
        });

        return session;
    }

    public async updatePassword(oldPassword: string, newPassword: string): Promise<any> {
        const response = await this.restService.updatePassword(oldPassword, newPassword);

        if (response.error) {
            const errors: any = {};

            if (response.errorCode && response.errorCode === 'InvalidOldCred') {
                errors.oldPassword = ['wrong current password'];
            }

            if (response.fieldErrors && response.fieldErrors.newpassword) {
                errors.newPassword = [response.fieldErrors.newpassword];
            }

            if (!_isEmpty(errors)) {
                throw new ValidationError(errors);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async forgotPasswordRequest(email: string): Promise<any> {
        const response = await this.restService.forgotPasswordRequest(email);

        if (response.error) {
            if (response.errorCode === 'PlayerDataNotCorrect') {
                throw new ValidationError({
                    email: ['E-mail not found']
                });
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    public async changeForgottenPassword(token: string, password: string): Promise<any> {
        const response = await this.restService.changeForgottenPassword(token, password);

        if (response.error) {
            if (response.fieldErrors && response.fieldErrors.newPassword) {
                throw new ValidationError({
                    password: [response.fieldErrors.newPassword]
                });
            } else if (response.errorCode === 'ChangePasswordTokenNotFound') {
                throw new MalformedDataError(response.notice);
            } else {
                throw NetworkUtils.getRequestError(response);
            }
        }
    }

    private createUser(data: any): User {
        const user = _pick(
            data,
            [
                'sessionId',
                'welcomeBonus',
                'balance',
                'currency',
            ]
        ) as User;

        user.isVerifiedIdCard = data.kycChecked;

        return {
            ...user,
            ...getDetails(data)
        };
    }
}

export function getDetails(data: any) {
    // TODO needs to set isTempAccount
    return {
        userId: data.userId,
        username: data.username,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        county: data.county,
        email: data.email,
        registrationDate: data.registrationDate,
        firstDepositDate: data.firstDepositDate,
        shouldVerifySms: data.shouldVerifySms,
        acceptsGeneralPrivacy: data.generalPrivacy,
        acceptsCustomPrivacy: data.customPrivacy,
    };
}

export const authenticationService = new AuthenticationService();



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/authentication/AuthenticationService.ts